import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  validateDocumentFile,
  getDocumentAcceptValue,
} from "../utils/documentValidation";
import settingsService from "../services/settingsService";
import styles from "../assets/settings.module.css";

const Settings = () => {
  const navigate = useNavigate();
  // State for form data
  const [formData, setFormData] = useState({
    on_boarding_process: null,
    hand_back_process: null,
  });

  // State for existing documents
  const [documents, setDocuments] = useState({
    on_boarding_process: { value: null, exists: false, s3_key: null },
    hand_back_process: { value: null, exists: false, s3_key: null },
  });

  // Loading states
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({
    on_boarding_process: false,
    hand_back_process: false,
  });

  // Refs for file inputs
  const onBoardingFileRef = useRef(null);
  const handBackFileRef = useRef(null);

  // Load settings data on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsService.getSettings();

      if (response.success && response.data) {
        setDocuments({
          on_boarding_process: response.data.on_boarding_process || {
            value: null,
            exists: false,
            s3_key: null,
          },
          hand_back_process: response.data.hand_back_process || {
            value: null,
            exists: false,
            s3_key: null,
          },
        });
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      toast.error("Failed to load settings. Using mock data.");

      // Fallback to mock data
      setDocuments(settingsService.getMockSettingsData());
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (field, event) => {
    const file = event.target.files[0];

    if (file) {
      // Validate file using utility
      const validation = validateDocumentFile(file);

      if (!validation.isValid) {
        toast.error(validation.error);
        event.target.value = "";
        setFormData((prev) => ({
          ...prev,
          [field]: null,
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));

      toast.success(`File "${file.name}" selected successfully.`);
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await settingsService.updateGeneralPropertyDocuments({
        on_boarding_process: formData.on_boarding_process,
        hand_back_process: formData.hand_back_process,
      });

      if (response.success) {
        toast.success("Settings updated successfully!");
        // Reload settings to get updated data
        await loadSettings();
        // Clear form data
        setFormData({
          on_boarding_process: null,
          hand_back_process: null,
        });
      } else {
        toast.error(response.message || "Failed to update settings.");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Network error while updating settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentOpen = async (s3Key, fileName) => {
    try {
      if (!s3Key) {
        toast.error("No document available to open");
        return;
      }

      // Try CDN URL first
      const cdnDomain = process.env.REACT_APP_S3_CDN_NAME;
      if (cdnDomain && cdnDomain !== "your-cdn-domain.com") {
        const cdnUrl = `https://${cdnDomain}/${s3Key}`;
        window.open(cdnUrl, "_blank");
        return;
      }

      // Fallback to signed URL
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const response = await fetch(
        `${baseUrl}/api/settings/document/${encodeURIComponent(
          fileName
        )}/signed-url`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.signedUrl) {
          window.open(data.signedUrl, "_blank");
        } else {
          toast.error("Failed to get document access URL");
        }
      } else {
        toast.error("Failed to open document");
      }
    } catch (error) {
      console.error("Error opening document:", error);
      toast.error("Network error while opening document");
    }
  };

  if (
    loading &&
    !documents.on_boarding_process &&
    !documents.hand_back_process
  ) {
    return (
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div
              className={
                styles.panel +
                " " +
                styles.panelDefault +
                " " +
                styles.panelStandard
              }
            >
              <div className={styles.panelBody + " " + styles.textCenter}>
                <div className={styles.spinnerBorder} role="status">
                  <span className={styles.srOnly}>Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Menu</span>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Settings</span>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div
            className={
              styles.panel +
              " " +
              styles.panelDefault +
              " " +
              styles.panelStandard
            }
          >
            <div
              className={styles.panelHeading + " " + styles.panelTitle}
              style={{ backgroundColor: "transparent" }}
            >
              <h3>Settings</h3>
            </div>
            <div className={styles.panelBody}>
              <form
                onSubmit={handleSubmit}
                id="form_settings"
                encType="multipart/form-data"
              >
                <fieldset
                  className={styles.formHorizontal + " " + styles.formBackend}
                >
                  {/* On boarding process */}
                  <div
                    className={styles.row}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <div className={styles.colXs4}>
                      {documents.on_boarding_process &&
                        documents.on_boarding_process.exists && (
                          <button
                            type="button"
                            className={styles.btnProperty + " " + styles.mt0}
                            onClick={() =>
                              handleDocumentOpen(
                                documents.on_boarding_process.s3_key,
                                documents.on_boarding_process.value
                              )
                            }
                          >
                            <div className={styles.documentLeft}>
                              <img src="/images/pdf.png" alt="PDF" />
                            </div>
                            <div className={styles.documentRight}>
                              <p className={styles.btnTitle}>
                                On boarding process
                              </p>
                              <p className={styles.btnShort}>
                                Click to open
                              </p>
                            </div>
                          </button>
                        )}
                    </div>
                    <div className={styles.colXs8}>
                      <div className={styles.formGroup}>
                        <label
                          htmlFor="on_boarding_process"
                          className={styles.controlLabel + " " + styles.colSm3}
                        >
                          On Boarding Process
                        </label>
                        <div className={styles.colSm9}>
                          <div className={styles.inputImage}>
                            <input
                              type="file"
                              name="on_boarding_process"
                              className={styles.formControl}
                              accept={getDocumentAcceptValue()}
                              onChange={(e) =>
                                handleFileChange("on_boarding_process", e)
                              }
                              disabled={uploading.on_boarding_process}
                              ref={onBoardingFileRef}
                            />
                            <span className={styles.helpBlock}>
                              If you don't want to change file - just leave the
                              field empty.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.clearfix}></div>

                  {/* Hand back process */}
                  <div
                    className={styles.row}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <div className={styles.colXs4}>
                      {documents.hand_back_process &&
                        documents.hand_back_process.exists && (
                          <button
                            type="button"
                            className={styles.btnProperty + " " + styles.mt0}
                            onClick={() =>
                              handleDocumentOpen(
                                documents.hand_back_process.s3_key,
                                documents.hand_back_process.value
                              )
                            }
                          >
                            <div className={styles.documentLeft}>
                              <img src="/images/pdf.png" alt="PDF" />
                            </div>
                            <div className={styles.documentRight}>
                              <p className={styles.btnTitle}>
                                Hand back process
                              </p>
                              <p className={styles.btnShort}>
                                Click to open
                              </p>
                            </div>
                          </button>
                        )}
                    </div>
                    <div className={styles.colXs8}>
                      <div className={styles.formGroup}>
                        <label
                          htmlFor="hand_back_process"
                          className={styles.controlLabel + " " + styles.colSm3}
                        >
                          Hand back Process
                        </label>
                        <div className={styles.colSm9}>
                          <div className={styles.inputImage}>
                            <input
                              type="file"
                              name="hand_back_process"
                              className={styles.formControl}
                              accept={getDocumentAcceptValue()}
                              onChange={(e) =>
                                handleFileChange("hand_back_process", e)
                              }
                              disabled={uploading.hand_back_process}
                              ref={handBackFileRef}
                            />
                            <span className={styles.helpBlock}>
                              If you don't want to change file - just leave the
                              field empty.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.clearfix}></div>
                </fieldset>

                <div className={styles.formGroup + " " + styles.textRight}>
                  <button
                    type="submit"
                    className={
                      styles.btn +
                      " " +
                      styles.btnHead +
                      " " +
                      styles.btnStandard
                    }
                    disabled={loading}
                  >
                    <i
                      className={styles.glyphicon + " " + styles.glyphiconOk}
                    ></i>{" "}
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
